import React, { useContext, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { AuthContext } from "../context/AuthContext";
import { FaTrash, FaPlus } from "react-icons/fa";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { markRecentGoogleSheet } from "../utils/googleSheetRecents";
import { extractErrorMessage } from "../utils/errorUtils";
import LinesheetSourceSelector from "./shared/LinesheetSourceSelector";
import InventorySourceSelector from "./shared/InventorySourceSelector";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";

import ToolInfoModal from "../components/common/ToolInfoModal";

function PurchaseOrderTool() {
  const [linesheetSource, setLinesheetSource] = useState("google");
  const [linesheet, setLinesheet] = useState(null);
  const [selectedGoogleSheet, setSelectedGoogleSheet] = useState(null);
  const [inventoryInputs, setInventoryInputs] = useState([
    { sheetName: "", rowsToProcess: "" },
  ]);
  const [inventoryFile, setInventoryFile] = useState(null);
  const [inventorySource, setInventorySource] = useState("upload");
  const [inventoryShopifyConfig, setInventoryShopifyConfig] = useState({
    location: "Main Store",
    vendors: "",
    excludeArchive: true,
    excludeRS: true
  });
  const [fetchingShopify, setFetchingShopify] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const { user } = useContext(AuthContext);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // ... (rest of existing state/functions)

  const toolInfo = {
    toolName: "Purchase Order",
    description: "Automatically generate purchase orders by matching linesheet items with inventory levels. This tool identifies items that need replenishing based on current stock.",
    steps: [
      "Choose a shared Google Sheet or upload your Linesheet (Excel).",
      "Choose your inventory source once using Upload Excel or Shopify Sync.",
      "Add one or more linesheet tabs you want to process.",
      "Click 'Generate Purchase Order' to download the result."
    ],
    templates: [
      { name: "Linesheet Template", link: "/templates/download/template_linesheet.xlsx" },
      { name: "Inventory Template", link: "/templates/download/template_inventory.xlsx" }
    ]
  };

  const guideTips = [
    "Shared Google Sheets are best for team-maintained linesheets because they stay available for everyone with access.",
    "Add one row per linesheet tab you want processed against the same inventory source.",
    "Recently used shared sheets appear at the top of the Google sheet list after a successful run.",
  ];

  const handleInventoryChange = (index, field, value) => {
    const updated = [...inventoryInputs];
    updated[index][field] = value;
    setInventoryInputs(updated);
  };

  const addInventoryRow = () => {
    setInventoryInputs([...inventoryInputs, { sheetName: "", rowsToProcess: "" }]);
  };

  const removeInventoryRow = (index) => {
    const updated = [...inventoryInputs];
    updated.splice(index, 1);
    setInventoryInputs(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasLinesheet =
      linesheetSource === "google"
        ? Boolean(selectedGoogleSheet?.id)
        : Boolean(linesheet);

    if (!hasLinesheet) {
      addToast("Please provide a linesheet.", "error");
      return;
    }
    
    // Check if sheetnames are mapped
    if (inventoryInputs.some((i) => !i.sheetName)) {
       addToast("Please provide a linesheet tab name for each row.", "error");
       return;
    }

    if (inventorySource === "upload" && !inventoryFile) {
      addToast("Please upload an inventory file.", "error");
      return;
    }

    // Check file size (500MB limit)
    const MAX_SIZE = 100 * 1024 * 1024;
    // Check linesheet
    if (linesheetSource === "upload" && linesheet && linesheet.size > MAX_SIZE) {
      addToast("Linesheet is too large. Maximum size is 100MB.", "error");
      return;
    }
    if (inventorySource === "upload" && inventoryFile && inventoryFile.size > MAX_SIZE) {
      addToast("Inventory file is too large. Maximum size is 100MB.", "error");
      return;
    }
    
    let finalInventoryFile = inventoryFile;
    
    if (inventorySource === "shopify") {
      setFetchingShopify(true);
      try {
        const excludeTags = [];
        if (inventoryShopifyConfig.excludeArchive) excludeTags.push("Archive");
        if (inventoryShopifyConfig.excludeRS) excludeTags.push("RS");
        
        const vendors = inventoryShopifyConfig.vendors
          ? inventoryShopifyConfig.vendors.split(",").map(v => v.trim()).filter(v => v)
          : [];
          
        const locationNames = inventoryShopifyConfig.location === "All" 
          ? [] 
          : [inventoryShopifyConfig.location];

        const shopifyRes = await flaskApi.post(
          "/shopify/fetch-inventory-excel", 
          { vendors, exclude_tags: excludeTags, location_names: locationNames },
          { responseType: "blob" }
        );
        
        finalInventoryFile = new File([shopifyRes.data], "shopify_inventory.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        
      } catch (err) {
        setFetchingShopify(false);
        const errorMessage = await extractErrorMessage(err, "Failed to fetch inventory from Shopify.");
        addToast(errorMessage, "error");
        return;
      }
      setFetchingShopify(false);
    }

    const formData = new FormData();
    if (linesheetSource === "google") {
      formData.append("google_linesheet_file_id", selectedGoogleSheet.id);
      formData.append("google_linesheet_file_name", selectedGoogleSheet.name);
    } else {
      formData.append("linesheet", linesheet);
    }
    
    inventoryInputs.forEach((input) => {
      formData.append("inventory[]", finalInventoryFile);
      formData.append("inventory_sheet_names[]", input.sheetName);
      formData.append("rows_to_process[]", input.rowsToProcess || "");
    });

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post(
        "/purchase/generate-purchase-order-ui",
        formData,
        {
          responseType: "blob",
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );


      // Check for JSON error (explicit)
      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      // Check for HTML error (proxy/server error)
      if (
        response.data.type === "text/html" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("text/html"))
      ) {
        const text = await response.data.text();
        // Extract error from title or body if possible, otherwise generic
        addToast("Server Error: Received HTML instead of file.", "error");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Extract filename from Content-Disposition header
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "Purchase_Order.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      if (linesheetSource === "google" && selectedGoogleSheet?.id) {
        markRecentGoogleSheet(user?.employeeId, selectedGoogleSheet);
      }

      addToast("File downloaded successfully.", "success");
    } catch (err) {
      const errorMessage = await extractErrorMessage(err, "Error generating purchase order.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <ToolPageLayout
        title="Purchase Order"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form tool-form-split">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Linesheet Tabs</h3>
              </div>

              <div className="tool-repeat-list">
                {inventoryInputs.map((input, index) => (
                  <div key={index} className="tool-repeat-card">
                    <div className="tool-repeat-grid">
                      <div className="tool-field-stack">
                        <label className="tool-label">Linesheet Tab Name</label>
                        <input
                          type="text"
                          className="tool-input"
                          placeholder="e.g. Birkenstock"
                          value={input.sheetName}
                          onChange={(event) =>
                            handleInventoryChange(index, "sheetName", event.target.value)
                          }
                          required
                        />
                      </div>
                      
                      <div className="tool-field-stack">
                        <label className="tool-label">Rows to Process (Optional)</label>
                        <input
                          type="text"
                          className="tool-input"
                          placeholder="e.g. 6-10, 12"
                          value={input.rowsToProcess || ""}
                          onChange={(event) =>
                            handleInventoryChange(index, "rowsToProcess", event.target.value)
                          }
                        />
                      </div>

                      {inventoryInputs.length > 1 ? (
                        <button
                          type="button"
                          className="tool-btn-icon"
                          onClick={() => removeInventoryRow(index)}
                          aria-label="Remove inventory row"
                        >
                          <FaTrash />
                        </button>
                      ) : (
                        <div />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="tool-add-row"
                onClick={addInventoryRow}
              >
                <FaPlus /> Add Linesheet Tab
              </button>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Inventory Source</h3>
              </div>
              <InventorySourceSelector
                source={inventorySource}
                onSourceChange={setInventorySource}
                uploadFile={inventoryFile}
                onUploadFileChange={setInventoryFile}
                shopifyConfig={inventoryShopifyConfig}
                onShopifyConfigChange={setInventoryShopifyConfig}
                uploadLabel="Inventory File"
              />
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={loading || fetchingShopify}
              >
                {loading || fetchingShopify ? "Processing..." : "Generate Purchase Order"}
              </button>

              <ToolProgressPanel
                loading={loading}
                isDownloadingShopify={fetchingShopify}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading linesheet and inventory files..."
                processingMessage="Processing Purchase Order... This may take a while."
              />
            </div>
          </div>

          <div className="tool-form-side">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Linesheet Source</h3>
              </div>
              <LinesheetSourceSelector
                source={linesheetSource}
                onSourceChange={setLinesheetSource}
                uploadFile={linesheet}
                onUploadFileChange={setLinesheet}
                selectedGoogleSheet={selectedGoogleSheet}
                onSelectedGoogleSheetChange={setSelectedGoogleSheet}
                uploadLabel="Linesheet"
              />
            </section>
          </div>
        </form>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}

export default PurchaseOrderTool;
